/**
 * Polling Service for AppBouncer SDK
 * Handles long-polling for async job completion with exponential backoff
 */

import { DEFAULT_POLLING, DEFAULT_TIMEOUTS } from '../config';

// ============================================================
// Types
// ============================================================

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface JobStatusResponse<T = unknown> {
  status: JobStatus;
  result?: T;
  error?: string;
  progress?: number;
}

// ============================================================
// Unified Verification Status Types
// ============================================================

export type VerificationStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
export type OcrJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface OcrStatusData {
  status: OcrJobStatus;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  document_type?: string;
  document_number?: string;
  expiry_date?: string;
  nationality?: string;
  age?: number;
  error?: string;
}

export interface FaceMatchData {
  matched: boolean;
  confidence: number;
  matched_at?: string;
}

export interface LivenessData {
  passed: boolean;
  score?: number;
  verified_at?: string;
}

export interface AgeData {
  estimated_age: number;
  confidence?: number;
  estimated_at?: string;
}

export interface VerificationStatusData {
  session_id: string;
  status: VerificationStatus;
  methods: string[];
  regime: string;
  country: string;
  country_name?: string;
  rule_name?: string;
  estimated_age?: number;
  document_type?: string;
  ocr?: OcrStatusData;
  face_match?: FaceMatchData;
  liveness?: LivenessData;
  age?: AgeData;
  created_at?: string;
  completed_at?: string;
  expires_at?: string;
}

export interface VerificationStatusResponse {
  code: number;
  success: boolean;
  data: VerificationStatusData;
}

export interface PollingConfig {
  /** API base URL */
  apiBaseUrl: string;
  /** Initial polling interval in ms (default: 1000) */
  initialInterval?: number;
  /** Maximum polling interval in ms (default: 30000) */
  maxInterval?: number;
  /** Backoff multiplier (default: 2.0) */
  backoffFactor?: number;
  /** Maximum total attempts before timeout (default: 60) */
  maxAttempts?: number;
  /** Jitter factor to randomize intervals (default: 0.1) */
  jitterFactor?: number;
  /** Include credentials in requests (default: true) */
  includeCredentials?: boolean;
  /** Request timeout in ms (default: 10000) */
  requestTimeout?: number;
}

export interface PollingCallbacks<T = unknown> {
  /** Called on each poll attempt */
  onPoll?: (attempt: number, elapsed: number) => void;
  /** Called when job status changes */
  onStatusChange?: (status: JobStatus) => void;
  /** Called on progress update (if available) */
  onProgress?: (progress: number) => void;
  /** Called on successful completion */
  onComplete?: (result: T) => void;
  /** Called on error */
  onError?: (error: PollingError) => void;
}

export interface PollOptions<T = unknown> {
  /** API key for authentication */
  apiKey?: string;
  /** Custom headers */
  headers?: Record<string, string>;
  /** Callbacks */
  callbacks?: PollingCallbacks<T>;
  /** Abort signal for cancellation */
  signal?: AbortSignal;
}

// ============================================================
// Default Configuration
// ============================================================

const DEFAULT_CONFIG: Required<Omit<PollingConfig, 'apiBaseUrl'>> = {
  initialInterval: DEFAULT_POLLING.initialInterval,
  maxInterval: DEFAULT_POLLING.maxInterval,
  backoffFactor: DEFAULT_POLLING.backoffFactor,
  maxAttempts: DEFAULT_POLLING.maxAttempts,
  jitterFactor: DEFAULT_POLLING.jitter,
  includeCredentials: true,
  requestTimeout: DEFAULT_TIMEOUTS.pollingRequest,
};

// ============================================================
// Polling Service Class
// ============================================================

export class PollingService {
  private config: Required<PollingConfig>;
  private activePolls: Map<string, AbortController> = new Map();

  constructor(config: PollingConfig) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };
  }

  /**
   * Poll for job completion
   * Returns when job completes or fails
   */
  async pollForResult<T = unknown>(
    taskId: string,
    endpoint: string,
    options: PollOptions<T> = {}
  ): Promise<T> {
    const { callbacks, signal } = options;

    // Create abort controller for this poll
    const controller = new AbortController();
    this.activePolls.set(taskId, controller);

    // Link external signal if provided
    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    let attempt = 0;
    let currentInterval = this.config.initialInterval;
    let lastStatus: JobStatus | null = null;
    const startTime = Date.now();

    try {
      while (attempt < this.config.maxAttempts) {
        if (controller.signal.aborted) {
          throw new PollingError('Polling cancelled', 'CANCELLED');
        }

        attempt++;
        const elapsed = Date.now() - startTime;
        callbacks?.onPoll?.(attempt, elapsed);

        try {
          const response = await this.fetchStatus<T>(taskId, endpoint, options, controller.signal);

          // Notify status change
          if (response.status !== lastStatus) {
            lastStatus = response.status;
            callbacks?.onStatusChange?.(response.status);
          }

          // Notify progress
          if (response.progress !== undefined) {
            callbacks?.onProgress?.(response.progress);
          }

          // Check for completion
          if (response.status === 'completed') {
            if (response.result === undefined) {
              throw new PollingError('Job completed but no result returned', 'NO_RESULT');
            }
            callbacks?.onComplete?.(response.result);
            return response.result;
          }

          // Check for failure
          if (response.status === 'failed') {
            throw new PollingError(response.error || 'Job failed', 'JOB_FAILED');
          }

          // Wait before next poll with exponential backoff and jitter
          await this.sleep(currentInterval, controller.signal);

          // Increase interval with backoff (up to max)
          currentInterval = Math.min(
            currentInterval * this.config.backoffFactor,
            this.config.maxInterval
          );
        } catch (error) {
          if (error instanceof PollingError) {
            throw error;
          }

          // Network errors - continue polling
          console.warn(`Poll attempt ${attempt} failed:`, error);

          // Wait before retry
          await this.sleep(currentInterval, controller.signal);
        }
      }

      // Max attempts reached
      throw new PollingError(`Polling timeout after ${attempt} attempts`, 'TIMEOUT');
    } finally {
      this.activePolls.delete(taskId);
    }
  }

  /**
   * Cancel an active poll
   */
  cancel(taskId: string): boolean {
    const controller = this.activePolls.get(taskId);
    if (controller) {
      controller.abort();
      this.activePolls.delete(taskId);
      return true;
    }
    return false;
  }

  /**
   * Cancel all active polls
   */
  cancelAll(): void {
    for (const [taskId, controller] of this.activePolls) {
      controller.abort();
      this.activePolls.delete(taskId);
    }
  }

  /**
   * Check if a poll is active
   */
  isPolling(taskId: string): boolean {
    return this.activePolls.has(taskId);
  }

  /**
   * Get count of active polls
   */
  getActiveCount(): number {
    return this.activePolls.size;
  }

  // ============================================================
  // Unified Status Polling
  // ============================================================

  /**
   * Poll for session verification status using the unified /v1/status endpoint.
   *
   * This is the recommended method for polling verification status.
   * The session_id is obtained from the /v1/requirements/check response.
   *
   * @param sessionId - Verification session ID (UUID7 from requirements check)
   * @param options - Polling options including API key and callbacks
   * @returns VerificationStatusData when session completes or fails
   */
  async pollForSessionStatus(
    sessionId: string,
    options: PollOptions<VerificationStatusData> = {}
  ): Promise<VerificationStatusData> {
    const { callbacks, signal } = options;

    // Create abort controller for this poll
    const controller = new AbortController();
    this.activePolls.set(sessionId, controller);

    // Link external signal if provided
    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    let attempt = 0;
    let currentInterval = this.config.initialInterval;
    let lastStatus: VerificationStatus | null = null;
    const startTime = Date.now();

    try {
      while (attempt < this.config.maxAttempts) {
        if (controller.signal.aborted) {
          throw new PollingError('Polling cancelled', 'CANCELLED');
        }

        attempt++;
        const elapsed = Date.now() - startTime;
        callbacks?.onPoll?.(attempt, elapsed);

        try {
          const response = await this.fetchSessionStatus(sessionId, options, controller.signal);

          const data = response.data;

          // Notify status change
          if (data.status !== lastStatus) {
            lastStatus = data.status;
            callbacks?.onStatusChange?.(data.status as JobStatus);
          }

          // Check for completion
          if (data.status === 'completed') {
            callbacks?.onComplete?.(data);
            return data;
          }

          // Check for failure
          if (data.status === 'failed') {
            const errorMsg = data.ocr?.error || 'Verification failed';
            throw new PollingError(errorMsg, 'JOB_FAILED');
          }

          // Wait before next poll with exponential backoff and jitter
          await this.sleep(currentInterval, controller.signal);

          // Increase interval with backoff (up to max)
          currentInterval = Math.min(
            currentInterval * this.config.backoffFactor,
            this.config.maxInterval
          );
        } catch (error) {
          if (error instanceof PollingError) {
            throw error;
          }

          // Network errors - continue polling
          console.warn(`Poll attempt ${attempt} failed:`, error);

          // Wait before retry
          await this.sleep(currentInterval, controller.signal);
        }
      }

      // Max attempts reached
      throw new PollingError(`Polling timeout after ${attempt} attempts`, 'TIMEOUT');
    } finally {
      this.activePolls.delete(sessionId);
    }
  }

  /**
   * Fetch session status from the unified endpoint
   */
  private async fetchSessionStatus(
    sessionId: string,
    options: PollOptions<VerificationStatusData>,
    signal: AbortSignal
  ): Promise<VerificationStatusResponse> {
    const url = `${this.config.apiBaseUrl}/v1/status/${sessionId}`;

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), this.config.requestTimeout);

    // Combine signals
    const combinedSignal = this.combineSignals(signal, timeoutController.signal);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (options.apiKey) {
        headers['X-API-Key'] = options.apiKey;
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: this.config.includeCredentials ? 'include' : 'omit',
        headers,
        signal: combinedSignal,
      });

      clearTimeout(timeoutId);

      if (response.status === 410) {
        throw new PollingError('Verification session has expired', 'TIMEOUT');
      }

      if (response.status === 404) {
        throw new PollingError('Verification session not found', 'JOB_FAILED');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new PollingError(
          errorData.detail || errorData.error || `HTTP ${response.status}`,
          'HTTP_ERROR',
          response.status
        );
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof PollingError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        if (signal.aborted) {
          throw new PollingError('Polling cancelled', 'CANCELLED');
        }
        throw new PollingError('Request timeout', 'REQUEST_TIMEOUT');
      }

      throw new PollingError(
        error instanceof Error ? error.message : 'Network error',
        'NETWORK_ERROR'
      );
    }
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private async fetchStatus<T>(
    taskId: string,
    endpoint: string,
    options: PollOptions<T>,
    signal: AbortSignal
  ): Promise<JobStatusResponse<T>> {
    const url = `${this.config.apiBaseUrl}${endpoint}/${taskId}`;

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), this.config.requestTimeout);

    // Combine signals
    const combinedSignal = this.combineSignals(signal, timeoutController.signal);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (options.apiKey) {
        headers['X-API-Key'] = options.apiKey;
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: this.config.includeCredentials ? 'include' : 'omit',
        headers,
        signal: combinedSignal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new PollingError(
          errorData.detail || errorData.message || `HTTP ${response.status}`,
          'HTTP_ERROR',
          response.status
        );
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof PollingError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        if (signal.aborted) {
          throw new PollingError('Polling cancelled', 'CANCELLED');
        }
        throw new PollingError('Request timeout', 'REQUEST_TIMEOUT');
      }

      throw new PollingError(
        error instanceof Error ? error.message : 'Network error',
        'NETWORK_ERROR'
      );
    }
  }

  private async sleep(ms: number, signal?: AbortSignal): Promise<void> {
    // Add jitter to prevent thundering herd
    const jitter = ms * this.config.jitterFactor * (Math.random() - 0.5) * 2;
    const actualMs = Math.max(100, ms + jitter);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(resolve, actualMs);

      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new PollingError('Sleep interrupted', 'CANCELLED'));
        });
      }
    });
  }

  private combineSignals(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();

    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort();
        break;
      }
      signal.addEventListener('abort', () => controller.abort());
    }

    return controller.signal;
  }
}

// ============================================================
// Error Class
// ============================================================

export type PollingErrorCode =
  | 'CANCELLED'
  | 'TIMEOUT'
  | 'JOB_FAILED'
  | 'NO_RESULT'
  | 'HTTP_ERROR'
  | 'NETWORK_ERROR'
  | 'REQUEST_TIMEOUT';

export class PollingError extends Error {
  public readonly code: PollingErrorCode;
  public readonly statusCode?: number;

  constructor(message: string, code: PollingErrorCode, statusCode?: number) {
    super(message);
    this.name = 'PollingError';
    this.code = code;
    this.statusCode = statusCode;
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return ['NETWORK_ERROR', 'REQUEST_TIMEOUT', 'HTTP_ERROR'].includes(this.code);
  }

  /**
   * Check if error is due to cancellation
   */
  isCancelled(): boolean {
    return this.code === 'CANCELLED';
  }

  /**
   * Check if error is a timeout
   */
  isTimeout(): boolean {
    return this.code === 'TIMEOUT' || this.code === 'REQUEST_TIMEOUT';
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create a PollingService instance
 */
export function createPollingService(config: PollingConfig): PollingService {
  return new PollingService(config);
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Calculate estimated time remaining based on current progress
 */
export function estimateTimeRemaining(startTime: number, progress: number): number | null {
  if (progress <= 0 || progress >= 100) {
    return null;
  }

  const elapsed = Date.now() - startTime;
  const rate = progress / elapsed;
  const remaining = (100 - progress) / rate;

  return Math.round(remaining);
}

/**
 * Format milliseconds to human-readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}
